/**
 * Хост уведомлений: единственное место, где тосты попадают на экран.
 *
 * Подписан на `toastStore`. На телефоне очередь сверху по центру, с `sm` —
 * в правом верхнем углу, под шапкой. Тост — стеклянная карточка в палитре
 * приложения: цветная полоса и иконка по тону (зелёный — успех, янтарный —
 * предупреждение, красный — ошибка, бирюзовый — информация). Въезжает
 * сбоку, а полоска внизу показывает, сколько ему осталось.
 */

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { useToastStore, type ToastTone } from '../store/toastStore';
import { IconAlertTriangle, IconCircleCheck, IconInfoCircle, IconX, type AppIcon } from './icons';

interface ToneStyle {
  readonly icon: AppIcon;
  /** Кружок иконки. */
  readonly badge: string;
  /** Полоса слева и полоска времени. */
  readonly bar: string;
}

const TONES: Record<ToastTone, ToneStyle> = {
  info: { icon: IconInfoCircle, badge: 'bg-teal/20 text-teal-deep dark:text-teal', bar: 'bg-teal' },
  success: { icon: IconCircleCheck, badge: 'bg-correct/20 text-correct', bar: 'bg-correct' },
  warning: { icon: IconAlertTriangle, badge: 'bg-accent/25 text-accent-dark dark:text-accent', bar: 'bg-accent' },
  error: { icon: IconAlertTriangle, badge: 'bg-danger/15 text-danger', bar: 'bg-danger' },
};

export function ToastHost() {
  const { t } = useTranslation();
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);
  const still = useReducedMotion() === true;

  return (
    <ol
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-3 z-[60] flex flex-col items-center gap-2 px-4 sm:inset-x-auto sm:right-5 sm:top-24 sm:items-end sm:px-0"
    >
      <AnimatePresence initial={false}>
        {toasts.map((item) => {
          const tone = TONES[item.tone];
          const Icon = tone.icon;
          return (
            <motion.li
              key={item.id}
              layout={!still}
              role={item.tone === 'error' ? 'alert' : 'status'}
              initial={still ? { opacity: 0 } : { opacity: 0, x: 48, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={still ? { opacity: 0 } : { opacity: 0, x: 48, scale: 0.96, transition: { duration: 0.18 } }}
              transition={{ type: 'spring', stiffness: 420, damping: 32 }}
              className="pointer-events-auto relative flex w-full max-w-sm items-start gap-3 overflow-hidden rounded-2xl border border-line bg-surface/80 py-3 pl-4 pr-2 shadow-card-lg backdrop-blur-xl sm:w-80"
            >
              <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-1 ${tone.bar}`} />
              <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${tone.badge}`}>
                <Icon size={18} stroke={2.2} aria-hidden="true" />
              </span>
              <p className="flex-1 self-center text-sm font-semibold leading-snug text-heading">{item.message}</p>
              <button
                type="button"
                onClick={() => dismiss(item.id)}
                aria-label={t('toast.close')}
                className="tap-target grid h-8 w-8 shrink-0 place-items-center rounded-full text-muted transition hover:bg-heading/10 hover:text-heading"
              >
                <IconX size={16} aria-hidden="true" />
              </button>
              {item.ttl !== null && !still && (
                <motion.span
                  aria-hidden="true"
                  initial={{ scaleX: 1 }}
                  animate={{ scaleX: 0 }}
                  transition={{ duration: item.ttl / 1000, ease: 'linear' }}
                  className={`absolute bottom-0 left-0 h-0.5 w-full origin-left opacity-60 ${tone.bar}`}
                />
              )}
            </motion.li>
          );
        })}
      </AnimatePresence>
    </ol>
  );
}
